import React, { useState, useEffect } from "react";
import { fetchData } from "../../../../utils/functions";
import ReactApexChart from "react-apexcharts";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import NoData, { hasData } from "../../../common/NoData";
import DashboardBarChart from "../../../common/DashboardBarChart";
import VaccinationSection from "./VaccinationSection";
import { getCategoryColors } from "../../../../utils/chartTheme";
import { formatCountFit } from "../../../../utils/numberFormat";

const hasSeries = (chartData) =>
  hasData((chartData?.data || []).map((item) => item.count));

const HealthStatusTab = ({ filters }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [data, setData] = useState({
    generalTreatment: { data: [], total: 0 },
    femaleTreatment: { data: [], total: 0 },
    birthStatus: { data: [], total: 0 },
    antenatalCheckup: { data: [], total: 0 },
    birthLocation: { data: [], total: 0 },
    maternalDeath: { data: [], total: 0 },
    specialNeeds: { data: [], total: 0, members: 0 },
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [
          generalTreatmentRes,
          femaleTreatmentRes,
          birthStatusRes,
          antenatalRes,
          birthLocationRes,
          maternalDeathRes,
          specialNeedsRes,
        ] = await Promise.all([
          fetchData("general-treatment-stats", "GET", null, {}, filters, navigate, "/general-treatment-stats", dispatch),
          fetchData("female-treatment-stats", "GET", null, {}, filters, navigate, "/female-treatment-stats", dispatch),
          fetchData("birth-status", "GET", null, {}, filters, navigate, "/birth-status", dispatch),
          fetchData("antenatal-checkup-stats", "GET", null, {}, filters, navigate, "/antenatal-checkup-stats", dispatch),
          fetchData("birth-location-stats", "GET", null, {}, filters, navigate, "/birth-location-stats", dispatch),
          fetchData("maternal-death-stats", "GET", null, {}, filters, navigate, "/maternal-death-stats", dispatch),
          fetchData("special-needs-stats", "GET", null, {}, filters, navigate, "/special-needs-stats", dispatch),
        ]);

        setData({
          generalTreatment: generalTreatmentRes || { data: [], total: 0 },
          femaleTreatment: femaleTreatmentRes || { data: [], total: 0 },
          birthStatus: birthStatusRes || { data: [], total: 0 },
          antenatalCheckup: antenatalRes || { data: [], total: 0 },
          birthLocation: birthLocationRes || { data: [], total: 0 },
          maternalDeath: maternalDeathRes || { data: [], total: 0 },
          specialNeeds: specialNeedsRes || { data: [], total: 0, members: 0 },
        });
      } catch (err) {
        console.error("Error loading Health Status tab:", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [filters, navigate, dispatch]);

  // pie chart options
  const createPieChartOptions = (labels, chartType = "default") => {
    return {
      chart: {
        type: "pie",
        height: 280,
      },
      plotOptions: { pie: { dataLabels: { minAngleToShowLabel: 18 } } },
      labels: labels,
      colors: getCategoryColors(labels),
      dataLabels: {
        enabled: true,
        style: { 
          fontSize: "11px", 
          fontWeight: "bold",
          colors: ["#fff"],
        },
        formatter: (val) => `${val.toFixed(1)}%`,
        dropShadow: {
          enabled: false,
        },
      },
      legend: {
        show: true,
        position: "bottom",
        fontSize: "10px",
        horizontalAlign: "center",
        offsetY: 0,
        markers: {
          width: 10,
          height: 10,
        },
      },
      tooltip: {
        enabled: false,
      },
    };
  };

  // donut chart options
  const createDonutChartOptions = (labels) => ({
    chart: {
      type: "donut",
      height: 280,
    },
    labels: labels,
    colors: getCategoryColors(labels),
    dataLabels: {
      enabled: true,
      style: { 
        fontSize: "11px", 
        fontWeight: "bold",
        colors: ["#fff"],
      },
      formatter: (val) => `${val.toFixed(1)}%`,
      dropShadow: {
        enabled: false,
      },
    },
    plotOptions: {
      pie: {
        dataLabels: { minAngleToShowLabel: 18 },
        donut: {
          size: "65%",
          labels: {
            show: true,
            value: { fontSize: "18px", fontWeight: 700, formatter: (val) => formatCountFit(val) },
            total: {
              show: true,
              label: "Total",
              formatter: (w) => formatCountFit(w.globals.seriesTotals.reduce((a, b) => a + b, 0)),
              fontSize: "14px",
              fontWeight: 600,
              color: "#374151",
            },
          },
        },
      },
    },
    legend: {
      show: true,
      position: "bottom",
      fontSize: "10px",
      horizontalAlign: "center",
      offsetY: 0,
      markers: {
        width: 10,
        height: 10,
      },
    },
    tooltip: {
      enabled: false,
    },
  });

  // Render bar chart section
  const renderChartSection = (title, chartData, chartType, subtitle = null) => {
    if (!hasSeries(chartData)) {
      return <NoData />;
    }
    return (
      <div className="h-full flex flex-col">
        <div className="mb-2 text-center flex-shrink-0">
          {subtitle && (
            <p className="text-xs text-gray-600 mt-1">{subtitle}</p>
          )}
        </div>
        <div className="flex-grow relative">
          <DashboardBarChart
            items={chartData.data}
            vertical={chartType === "treatment" || chartType === "female"}
            tilt={chartType === "treatment" || chartType === "female"}
          />
        </div>
      </div>
    );
  };

  // Render pie chart section
  const renderPieChartSection = (title, chartData, subtitle = null, chartType = "default") => {
    if (!hasSeries(chartData)) {
      return <NoData />;
    }

    const labels = chartData.data.map((item) => item.name);
    const values = chartData.data.map((item) => item.count);
    const chartOptions = createPieChartOptions(labels, chartType);

    return (
      <div className="h-full flex flex-col">
        <div className="mb-2 text-center flex-shrink-0">
          {subtitle && (
            <p className="text-xs text-gray-600 mt-1">{subtitle}</p>
          )}
        </div>
        <div className="flex-grow">
          <ReactApexChart
            options={chartOptions}
            series={values}
            type="pie"
            height={280}
          />
        </div>
      </div>
    );
  };

  // Render donut chart section
  const renderDonutChartSection = (title, chartData, subtitle = null) => {
    if (!hasSeries(chartData)) {
      return <NoData />;
    }

    const labels = chartData.data.map((item) => item.name);
    const values = chartData.data.map((item) => item.count);
    const chartOptions = createDonutChartOptions(labels);

    return (
      <div className="h-full flex flex-col">
        <div className="mb-2 text-center flex-shrink-0">
          {subtitle && (
            <p className="text-xs text-gray-600 mt-1">{subtitle}</p>
          )}
        </div>
        <div className="flex-grow">
          <ReactApexChart
            options={chartOptions}
            series={values}
            type="donut"
            height={280}
          />
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#137E81]"></div>
        <p className="mt-4 text-gray-600">Loading health status data...</p>
      </div>
    );
  }

  const has = {
    generalTreatment: hasSeries(data.generalTreatment),
    femaleTreatment: hasSeries(data.femaleTreatment),
    birthStatus: hasSeries(data.birthStatus),
    antenatalCheckup: hasSeries(data.antenatalCheckup),
    birthLocation: hasSeries(data.birthLocation),
    maternalDeath: hasSeries(data.maternalDeath),
    specialNeeds: hasSeries(data.specialNeeds) || (data.specialNeeds.noDisability?.total || 0) > 0,
  };

  const anyData = Object.values(has).some(Boolean);

  const pwdRows = [
    ...data.specialNeeds.data.map((d) => ({
      label: d.name.replace(/ disability$/i, ""),
      male: d.male || 0,
      female: d.female || 0,
    })),
    {
      label: "No disability",
      male: data.specialNeeds.noDisability?.male || 0,
      female: data.specialNeeds.noDisability?.female || 0,
    },
  ];

  return (
    <div className="space-y-6 pb-8">
      {/* Row 1: Treatment Seeking Behavior */}
      {anyData && (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* General Treatment */}
        <div className="bg-white flex flex-col">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            General Treatment Facilities
          </h3>
          <div className="flex-grow">
            {renderChartSection("General Treatment", data.generalTreatment, "treatment", "Where households seek treatment")}
          </div>
        </div>

        {/* Female Treatment */}
        <div className="bg-white flex flex-col lg:col-span-2">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Female Treatment Facilities
          </h3>
          <div className="flex-grow">
            {renderChartSection("Female Treatment", data.femaleTreatment, "female", "Where female household members seek treatment")}
          </div>
        </div>
      </div>
      )}

      {/* Row 2: Birth Information */}
      {anyData && (<>
      <hr className="border-t-2 border-dashed border-teal-600 first:hidden" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Birth Status - Pie Chart */}
        <div className="bg-white flex flex-col">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Birth Status
          </h3>
          <div className="flex-grow">
            {renderPieChartSection("Birth Status", data.birthStatus, "Women who gave birth in last 3 years", "birthStatus")}
          </div>
        </div>

        {/* Antenatal Checkup - Donut Chart */}
        <div className="bg-white flex flex-col">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Antenatal Checkup
          </h3>
          <div className="flex-grow">
            {renderDonutChartSection("Antenatal Checkup", data.antenatalCheckup, "Women who received checkups")}
          </div>
        </div>

        {/* Birth Location - Bar Chart */}
        <div className="bg-white flex flex-col">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Birth Location
          </h3>
          <div className="flex-grow">
            {renderChartSection("Birth Location", data.birthLocation, "birth", "Where births occurred")}
          </div>
        </div>
      </div>
      </>)}

      {/* Row 3: Child vaccination, special needs and maternal mortality */}
      <>
      <hr className="border-t-2 border-dashed border-teal-600 first:hidden" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div>
          <VaccinationSection filters={filters} />
        </div>
        <div className="bg-white flex flex-col">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            PwD
          </h3>
          <p className="text-center text-sm text-gray-600 mb-4">
            Persons with Disabilities by Gender
          </p>
          <div className="flex-grow">
            {has.specialNeeds ? (
              <DashboardBarChart
                horizontal
                categories={pwdRows.map((row) => row.label)}
                series={[
                  { name: "Male", data: pwdRows.map((row) => row.male) },
                  { name: "Female", data: pwdRows.map((row) => row.female) },
                ]}
                unit="persons"
              />
            ) : (
              <NoData />
            )}
          </div>
        </div>
        <div className="bg-white flex flex-col">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Maternal Mortality
          </h3>
          <div className="flex-grow">
            {renderPieChartSection("Maternal Death", data.maternalDeath, "Deaths during pregnancy/childbirth", "maternal")}
          </div>
        </div>
      </div>
      </>
    </div>
  );
};

export default HealthStatusTab;